/**
 * Countdown timer custom element.
 * Expects `data-end` as an ISO-8601 datetime or `YYYY-MM-DD HH:MM` string.
 */
class CountdownTimer extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | null} */
  #interval = null;

  /** @type {number | null} */
  #endMs = null;

  connectedCallback() {
    this.#endMs = this.#parseEnd(this.dataset.end);
    if (this.#endMs == null) {
      this.hidden = true;
      return;
    }

    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    if (this.#interval != null) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  }

  /**
   * @param {string | undefined} value
   * @returns {number | null}
   */
  #parseEnd(value) {
    if (!value) return null;

    const normalized = value.trim().replace(' ', 'T');
    const parsed = Date.parse(normalized);

    if (Number.isNaN(parsed)) return null;
    return parsed;
  }

  #tick() {
    if (this.#endMs == null) return;

    const remaining = this.#endMs - Date.now();
    const complete = remaining <= 0;

    if (complete) {
      this.#render(0, 0, 0, 0);
      this.dataset.complete = 'true';

      if (this.dataset.hideWhenComplete === 'true') {
        this.hidden = true;
      }

      const completeMessage = this.querySelector('.countdown-timer__complete');
      const units = this.querySelector('.countdown-timer__units');
      if (completeMessage instanceof HTMLElement) {
        completeMessage.hidden = false;
      }
      if (units instanceof HTMLElement && this.dataset.hideUnitsWhenComplete === 'true') {
        units.hidden = true;
      }

      if (this.#interval != null) {
        clearInterval(this.#interval);
        this.#interval = null;
      }
      return;
    }

    this.dataset.complete = 'false';
    this.hidden = false;

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#render(days, hours, minutes, seconds);
  }

  /**
   * @param {number} days
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   */
  #render(days, hours, minutes, seconds) {
    this.#setUnit('days', days);
    this.#setUnit('hours', hours);
    this.#setUnit('minutes', minutes);
    this.#setUnit('seconds', seconds);
  }

  /**
   * @param {string} name
   * @param {number} value
   */
  #setUnit(name, value) {
    const el = this.querySelector(`[data-unit="${name}"]`);
    if (!(el instanceof HTMLElement)) return;

    const padded = String(value).padStart(2, '0');
    if (el.textContent !== padded) {
      el.textContent = padded;
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
